import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for fetching widget data from API
 * Handles loading, error states, and automatic refetching
 * 
 * @param {string} url - API endpoint URL
 * @param {string} requestJson - Primary request body (JSON string)
 * @param {string} requestJson1 - Secondary request data (filter JSON string)
 * @returns {Object} { data, loading, error, refetch }
 */
export function useWidgetData(url, requestJson, requestJson1) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!url || !requestJson) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': requestJson1 || ''
                },
                body: requestJson
            };

            const response = await fetch(url, requestOptions);
            const text = await response.text();
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = text ? JSON.parse(text) : [];
            setData(apiData);
        } catch (err) {
            console.error('Error fetching widget data:', err);
            setError(err.message || 'Failed to fetch data');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [url, requestJson, requestJson1]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData
    };
}

export default useWidgetData;
